import { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  bootstrapChartData,
  defaultOptions,
  getTimeScaleOptions
} from 'components/charts';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { cloneDeep, merge } from 'lodash';
import {
  createModal,
  DateRangePicker,
  LoadingIndicator,
  Tooltip
} from '@thermon-platforms/thermon-ui-core';
import ConfigModal from 'components/charts/settings-modal';
import api from 'util/api';
import useSessionStore from 'store/session';


const styles = {
  Temperature: {
    label: 'Temperature',
    borderColor: '#ff9f40',
    yAxisID: 'y'
  },
  Current: {
    label: 'Current',
    borderColor: '#36a2eb',
    yAxisID: 'y'
  },
  GroundCurrent: {
    label: 'Ground Current',
    borderColor: '#ffcd56',
    yAxisID: 'y1'
  },
  default: {
    borderColor: '#aaa',
    yAxisID: 'y'
  },
}

function parseHistoricalData(data) {
  // We split the chart into two parts, temperature and current
  const parsedData = {
    temperature: {
      datasets: [],
    },
    current: {
      datasets: [],
    }
  }

  // Add primary data
  const readings = data.readingsDataByType;
  if (!readings) return parsedData;
  const keys = Object.keys(readings);
  keys.forEach((key, i) => {
    if (key == 'Voltage') return;
    const which = key == 'Temperature' ? 'temperature' : 'current';

    parsedData[which].datasets.push(Object.assign({}, styles[key], {
      data: readings[key].data.map(item => {
        return {x: item.time, y: item.value}
      })
    }));
  });

  // Add set point info
  // parsedData.temperature.datasets.push(Object.assign({}, styles.default, {
  //   label: t`Maintain`,
  //   spanGaps: true,
  //   data: data?.maintainSetpointData?.data?.map(item => {
  //     return {x: item.time, y: item.value}
  //   })
  // }));
  return parsedData;
}

const RtdColors = ['#999999', '#777777', '#888888', '#666666', '#555555'];

/**
 * @function HistoricalChart
 * @description line chart for circuit historical temp and current
 * @memberof module:view/Devices/Detail
 *
 * @param {object} props
 * @param {object} props.data
 * @returns React.Element
 *
 * @example
 * <HistoricalChart data={data} loading={loading} />
 */
export function getDownloadSafeName(circuitName) {
  const safeName = String(circuitName || '').trim();
  if (!safeName) return 'circuit';

  return safeName
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase() || 'circuit';
}

export function drawCircuitTitle(ctx, { width, circuitName, offset = 12, isDarkMode = true }) {
  const displayName = String(circuitName || '').trim();
  if (!ctx || !displayName) return;

  ctx.save();
  ctx.font = '600 18px "Roboto", sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillStyle = isDarkMode ? '#fff' : '#333';
  ctx.fillText(displayName, width / 2, offset);
  ctx.restore();
}

function HistoricalChart({circuitIds, deviceIndices, alarmBands, showCurrent=true, circuitName}) {
  const [sessionStore] = useSessionStore();
  const now = new Date();
  const [range, setRange] = useState({
    start: new Date(now - (24 * 60 * 60 * 1000)).toISOString(),
    end: new Date(now).toISOString()
  });
  const [temperatureData, setTemperatureData] = useState(bootstrapChartData());
  const [currentData, setCurrentData] = useState(bootstrapChartData());
  const [loading, setLoading] = useState(false);
  const [tempUnit, setTempUnit] = useState();
  const padding = 15;
  // To prevent race condition issues, live is a ref and not state
  const live = useRef(true);

  const tempChartRef = useRef(null);
  const currentChartRef = useRef(null);
  const isSyncingRef = useRef(false);

  function syncXFromSource(sourceRef, targetRef) {
    const source = sourceRef.current;
    const target = targetRef.current;
    if (!source || !target) return;

    const s = source.scales?.x;
    const t = target.scales?.x;
    if (!s || !t) return;

    // If user reset zoom, clear target bounds too
    const isReset =
      typeof source.getZoomLevel === 'function' && source.getZoomLevel() === 1 &&
      s?.options?.min == null && s?.options?.max == null;

    isSyncingRef.current = true;
    if (isReset) {
      t.options.min = undefined;
      t.options.max = undefined;
    } else {
      // Prefer user-set bounds; fall back to current visible bounds
      const newMin = s?.options?.min ?? s.min;
      const newMax = s?.options?.max ?? s.max;
      t.options.min = newMin;
      t.options.max = newMax;
    }
    target.update('none');
    isSyncingRef.current = false;
  }

  // Make sure we handle any clean up
  const interval = useRef();
  useEffect(() => {
    interval.current = setInterval(setRangeToNow, 1 * 60 * 1000);
    return () => {
      if (interval.current) clearInterval(interval.current);
    }
  }, []);

  function queryRTDData(deviceIndex) {
    return api(`/historicalData?startDate=${range.start}&endDate=${range.end}&circuitIds=${circuitIds[0]}&deviceIndex=${deviceIndex}`)
      .then(async res => {
        const rawData = await res.json();
        const data = rawData[circuitIds[0]]?.readingsDataByType?.Temperature?.data?.map(d => ({
          x: d.time,
          y: d.value
        }));

        if (!data?.length) return null;

        return {
          borderColor: RtdColors[(deviceIndex - 1 + RtdColors.length) % RtdColors.length],
          label: `RTD ${deviceIndex}`, // WIP Need to figure out what the actual indexes represent
          yAxisID: 'y',
          data
        };
      });
  }

  function queryData() {
    setLoading(true);
    const fidelity = Math.round(((new Date(range.end) - new Date(range.start)) / (60 * 1000)) * Number(sessionStore.chartFidelity));

    api(`/historicalData?startDate=${range.start}&endDate=${range.end}&circuitIds=${circuitIds[0]}&maxDataPoints=${fidelity}`)
      .then(async res => {
        const rawData = await res.json();
        setTempUnit(rawData[circuitIds[0]]?.readingsDataByType?.Temperature?.units);

        const allData = parseHistoricalData(rawData[circuitIds[0]]);

        if (deviceIndices?.length > 1) {
          const rtdSeries = await Promise.all(
            // Index 0 is the circuit itself, so skip it
            deviceIndices?.filter(index => index > 0)
              .map(index => queryRTDData(index))
          );

          allData.temperature.datasets.push(...rtdSeries.filter(Boolean));
        }

        setTemperatureData(allData.temperature);
        setCurrentData(allData.current);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  // Handle side effects of the date range changing
  useEffect(() => {
    // If end date is within a minute of now, let's assume it's live
    const now = new Date();
    if (now - new Date(range.end) < (1 * 60 * 1000)) {
      live.current = true
    } else {
      live.current = false;
    }
    queryData();
  }, [range]);

  function setRangeToNow() {
    const now = new Date();
    setRange({
      start: range.start || new Date(now - (24 * 60 * 60 * 1000)).toISOString(),
      end: new Date(now).toISOString()
    });
  }

  function setLive () {
    if (interval.current) clearInterval(interval.current);
    setRangeToNow();
    interval.current = setInterval(setRangeToNow, 1 * 60 * 1000);
    live.current = true;
  }

  function pauseLive() {
    if (interval.current) clearInterval(interval.current);
    live.current = false;
  }

  function getMin(data = [], defaultValue) {
    if (!data || data.length === 0) return defaultValue;
    return Math.min(data.reduce((min, d) => (d.y < min ? d.y : min), defaultValue), defaultValue) - padding;
  }

  function getMax(data = [], defaultValue) {
    if (!data || data.length === 0) return defaultValue;
    return Math.max(data.reduce((max, d) => (d.y > max ? d.y : max), defaultValue), defaultValue) + padding;
  }

  // Wait for the chart fidelity to change before re-querying data
  const firstLoad = useRef(true);
  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      return;
    }
    queryData();
  }, [sessionStore.chartFidelity]);

  function configureCharts() {
    const configModal = createModal(<ConfigModal />)

    configModal.open()
      .catch(() => {/* no-op */});
  }

  function downloadChart() {
    const tempChart = tempChartRef.current;
    const currentChart = currentChartRef.current;
    if (!tempChart) return;

    const titleHeaderHeight = 32;
    const titleOffset = 10;
    const chartFileName = getDownloadSafeName(circuitName);

    // Default to dark mode, only switch to light when explicitly preferred
    let isDarkMode = true;
    if (typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: light)').matches
    ) {
      isDarkMode = false;
    }

    // If we only have the temperature chart, download it directly with the circuit name in the header
    if (!currentChart || !showCurrent) {
      const canvas = document.createElement('canvas');
      const tempCanvas = tempChart.canvas;
      canvas.width = tempCanvas.width;
      canvas.height = tempCanvas.height + titleHeaderHeight;

      const ctx = canvas.getContext('2d'); if (!ctx) return;
      ctx.fillStyle = isDarkMode ? 'rgba(36, 36, 36, 1)' : '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      drawCircuitTitle(ctx, { width: canvas.width, circuitName, offset: titleOffset, isDarkMode });
      ctx.drawImage(tempCanvas, 0, titleHeaderHeight);

      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `temperature-chart-${chartFileName}-${new Date().toISOString()}.png`;
      link.click();
      return;
    }

    // Combine both charts into a single image with the circuit name in the header
    const tempCanvas = tempChart.canvas;
    const currentCanvas = currentChart.canvas;
    const combinedCanvas = document.createElement('canvas');
    const spacing = 20;
    combinedCanvas.width = Math.max(tempCanvas.width, currentCanvas.width);
    combinedCanvas.height = tempCanvas.height + currentCanvas.height + spacing + titleHeaderHeight;

    const ctx = combinedCanvas.getContext('2d');
    ctx.fillStyle = isDarkMode ? 'rgba(36, 36, 36, 1)' : '#ffffff';
    ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);
    drawCircuitTitle(ctx, { width: combinedCanvas.width, circuitName, offset: titleOffset, isDarkMode });
    ctx.drawImage(tempCanvas, 0, titleHeaderHeight);
    ctx.drawImage(currentCanvas, 0, titleHeaderHeight + tempCanvas.height + spacing);

    const link = document.createElement('a');
    link.href = combinedCanvas.toDataURL('image/png');
    link.download = `historical-charts-${chartFileName}-${new Date().toISOString()}.png`;
    link.click();
  }

  return (
    <>
      <div className="chart-container">
        <div className="controls">
          <DateRangePicker
            start={range.start}
            end={range.end}
            onChange={setRange}
          />
          <button
            onClick={configureCharts}
            className="settings"
            data-tooltip-content={t`Chart Settings`}
            data-tooltip-id="chart-tooltip"
            aria-label={t`Chart Settings`}
          >
            <Trans>Chart Settings</Trans>
          </button>
          <button
            onClick={downloadChart}
            className="download"
            data-tooltip-content={t`Download Chart`}
            data-tooltip-id="chart-tooltip"
            aria-label={t`Download temperature chart as PNG`}
          >
            <Trans>Download</Trans>
          </button>
          <LoadingIndicator show={loading} />
          <span
            className={`live-indicator ${live.current ? 'active' : ''}`}
            onClick={setLive}
          >
            <Trans>Live</Trans>
          </span>
        </div>
        <div className="chart">
          <Line
            ref={tempChartRef}
            data={temperatureData}
            options={
              merge(cloneDeep(defaultOptions.line), {
                spanGaps: 1000 * 60 * 60,
                plugins: {
                  zoom: {
                    pan: {
                      enabled: true,
                      mode: 'xy',
                      onPan: () => {
                        if (!isSyncingRef.current) syncXFromSource(tempChartRef, currentChartRef);
                      },
                      onPanComplete: () => {
                        if (!isSyncingRef.current) syncXFromSource(tempChartRef, currentChartRef);
                        pauseLive();
                      }
                    },
                    zoom: {
                      wheel: { enabled: true },
                      pinch: { enabled: true },
                      mode: 'xy',
                      onZoom: () => {
                        if (!isSyncingRef.current) syncXFromSource(tempChartRef, currentChartRef);
                      },
                      onZoomComplete: () => {
                        if (!isSyncingRef.current) syncXFromSource(tempChartRef, currentChartRef);
                        pauseLive();
                      }
                    }
                  },
                  colorBands: {
                    bands: [
                      { axis: 'y', from: alarmBands?.temperature?.low, to: alarmBands?.temperature?.low - padding, color: 'rgba(148,236,255,0.20)' },
                      { axis: 'y', from: alarmBands?.temperature?.high, to: (alarmBands?.temperature?.trip || (alarmBands?.temperature?.high + padding)), color: 'rgba(243,220,49,0.20)' },
                      { axis: 'y', from: alarmBands?.temperature?.trip, to: alarmBands?.temperature?.trip + padding, color: 'rgba(218,41,28,0.20)' },
                    ]
                  },
                },
                scales: {
                  x: getTimeScaleOptions(),
                  y: {
                    min: getMin(temperatureData?.datasets?.[0]?.data, alarmBands?.temperature?.low),
                    max: getMax(temperatureData?.datasets?.[0]?.data, (alarmBands?.temperature?.trip || alarmBands?.temperature?.high)),
                    type: 'linear',
                    position: 'left',
                    title: { display: true, text: t`Temperature` + ` (${tempUnit || '°C'})` }
                  },
                  y1: {
                    min: getMin(temperatureData?.datasets?.[0]?.data, alarmBands?.temperature?.low),
                    max: getMax(temperatureData?.datasets?.[0]?.data, (alarmBands?.temperature?.trip || alarmBands?.temperature?.high)),
                    position: 'right',
                    title: { display: true, text: t`Temperature` + ` (${tempUnit || '°C'})` }
                  }
                }
              })
            }
          />
        </div>
        {showCurrent ? <div className="chart">
          <Line
            ref={currentChartRef}
            data={currentData}
            options={
              merge(cloneDeep(defaultOptions.line), {
                spanGaps: 1000 * 60 * 60,
                plugins: {
                  zoom: {
                    pan: {
                      enabled: true,
                      mode: 'xy',
                      onPan: () => {
                        if (!isSyncingRef.current) syncXFromSource(currentChartRef, tempChartRef);
                      },
                      onPanComplete: () => {
                        if (!isSyncingRef.current) syncXFromSource(currentChartRef, tempChartRef);
                        pauseLive();
                      }
                    },
                    zoom: {
                      wheel: { enabled: true },
                      pinch: { enabled: true },
                      mode: 'xy',
                      onZoom: () => {
                        if (!isSyncingRef.current) syncXFromSource(currentChartRef, tempChartRef);
                      },
                      onZoomComplete: () => {
                        if (!isSyncingRef.current) syncXFromSource(currentChartRef, tempChartRef);
                        pauseLive();
                      }
                    }
                  },
                  // colorBands: {
                  //   bands: [
                  //     { axis: 'y', from: alarmBands?.current?.low, to: alarmBands?.current?.low - padding, color: 'rgba(148,236,255,0.08)' },
                  //     { axis: 'y', from: alarmBands?.current?.high, to: alarmBands?.current?.trip, color: 'rgba(243,220,49,0.08)' },
                  //     { axis: 'y', from: alarmBands?.current?.trip, to: alarmBands?.current?.trip + padding, color: 'rgba(218,41,28,0.08)' },
                  //   ]
                  // },
                },
                scales: {
                  x: getTimeScaleOptions(),
                  y: {
                    min: 0,
                    max: (alarmBands?.current?.trip || 0) + padding,
                    type: 'linear',
                    position: 'left',
                    title: { display: true, text: t`Current` + ' (A)' }
                  },
                  y1: {
                    min: 0,
                    max: 20,
                    type: 'linear',
                    position: 'right',
                    title: { display: true, text: t`Ground Current` + ' (mA)' },
                    grid: { drawOnChartArea: false }
                  }
                }
              })
            }
          />
        </div> : null}
      </div>
      <Tooltip id="chart-tooltip" />
    </>
  );
}

export default HistoricalChart;
